/**
 * Shared demo-booking grid, consumed by both the public page and the API routes.
 * Keep this module free of `process.env` and server-only imports: it is pulled into
 * the client bundle by the landing page.
 */

export const SLOT_INTERVAL_MINUTES = 30;
export const MEETING_MINUTES = 30;
export const BOOKING_HORIZON_DAYS = 60;

/** Earliest a demo may be booked, relative to now — the host needs a night to prepare. */
export const DEMO_LEAD_MINUTES = 16 * 60;

/** Earliest an interview may be booked, relative to now. */
export const INTERVIEW_LEAD_MINUTES = 120;

/** Europe/Brussels business windows, [startInclusive, endExclusive) wall clock. */
export const BUSINESS_WINDOWS = [
  ['09:00', '12:00'],
  ['13:00', '19:00'],
] as const;

/** Interviews run on their own hours, outside the working day the demo grid covers. */
export const INTERVIEW_WINDOWS = [
  ['12:00', '14:00'],
  ['16:00', '20:00'],
] as const;

/* …and only Tuesday to Friday — that rule is a calendar one, so it lives with the other
   weekday helpers in brussels-time (isInterviewDay). */

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const toHhmm = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Every candidate start whose meeting still ends inside one of the windows — derived,
 * so widening a window or lengthening a meeting cannot leave a stale grid behind.
 */
const slotsWithin = (windows: readonly (readonly [string, string])[]): readonly string[] =>
  windows.flatMap(([open, close]) => {
    const starts: string[] = [];
    const last = toMinutes(close) - MEETING_MINUTES;
    for (let t = toMinutes(open); t <= last; t += SLOT_INTERVAL_MINUTES) starts.push(toHhmm(t));
    return starts;
  });

export const BOOKING_SLOTS = slotsWithin(BUSINESS_WINDOWS);
export const INTERVIEW_SLOTS = slotsWithin(INTERVIEW_WINDOWS);

/** Boundary between the two interview windows, for grouping the slot list in the UI. */
export const INTERVIEW_LATE_FROM = INTERVIEW_WINDOWS[1][0];

export const isBookingSlot = (time: string): boolean => BOOKING_SLOTS.includes(time);
export const isInterviewSlot = (time: string): boolean => INTERVIEW_SLOTS.includes(time);
